import type { PickEnsure } from '#layers/base/public';
import type { BlockSerialized } from '#layers/block-engine/types/block';
import { isBlockSerializedChild, isBlockSerializedSlotOutlet } from '#layers/block-engine/utils/block/editor';

export interface ProcessRemoveBlockOptions {
	editedBlock: Pick<BlockSerialized, 'children' | 'rootChildren'>;
	childId: string;
	parentChildId?: string | null;
	slot?: string;
}

export function processRemoveBlock(options: ProcessRemoveBlockOptions): {
	data: Partial<BlockSerialized>;
} {
	const {
		editedBlock,
		childId,
		parentChildId = null,
		slot,
	} = options;

	const data: PickEnsure<BlockSerialized, 'children' | 'rootChildren'> = {
		children: { ...editedBlock.children },
		rootChildren: editedBlock.rootChildren ?? [],
	};

	// Remove all children recursively
	const child = data.children[childId];

	if (isBlockSerializedChild(child)) {
		for (const slotChildren of Object.values(child.slots)) {
			for (const slotChildId of slotChildren) {
				const { data: subData } = processRemoveBlock({
					editedBlock: data,
					childId: slotChildId,
					parentChildId: childId,
				});

				Object.assign(data, subData);
			}
		}
	}

	delete data.children[childId];

	if (parentChildId) {
		const parentChild = data.children?.[parentChildId];

		if (!parentChild) {
			throw new Error(`Parent child with id ${parentChildId} not found`);
		}

		let newParentChild: typeof parentChild;

		if (isBlockSerializedChild(parentChild)) {
			const finalSlot = slot ?? Object.keys(parentChild.slots).find((slot) => parentChild.slots[slot]!.includes(childId)) ?? 'default';

			// Block
			newParentChild = {
				...parentChild,
				slots: {
					...parentChild.slots,
					[finalSlot]: (parentChild.slots[finalSlot] ?? []).filter((id) => id !== childId),
				},
			};
		}
		else if (isBlockSerializedSlotOutlet(parentChild)) {
			// Slot outlet
			newParentChild = {
				...parentChild,
				fallback: (parentChild.fallback ?? []).filter((id) => id !== childId),
			};
		}
		else {
			throw new Error(`Invalid parent child with id ${parentChildId}`);
		}

		data.children = {
			...data.children,
			[parentChildId]: newParentChild,
		};
	}
	else {
		data.rootChildren = (editedBlock.rootChildren ?? []).filter((id) => id !== childId);
	}

	return {
		data,
	};
}
